import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  formatDistanceToNow,
  isSameDay,
  isToday,
  isYesterday,
  roundToNearestMinutes,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';

import { DateTimeValue, PREDEFINED_DATE_RANGE } from '@/types/common';

/**
 * Calculates the start and end dates for a given date range keyword.
 *
 * Supported range values:
 * - 'today': The current date.
 * - 'yesterday': The previous day.
 * - 'this_week': The current week (Monday to Sunday).
 * - 'this_month': The current month.
 * - 'last_month': The previous month.
 * - 'this_quarter': The current quarter.
 * - 'last_quarter': The previous quarter.
 * - 'this_year': The current year.
 * - 'last_year': The previous year.
 *
 * If an unsupported range is provided, defaults to the current month.
 *
 * @param range - A string representing the desired date range.
 * @returns An object containing `fromDate` and `toDate` as formatted strings ('yyyy-MM-dd').
 */
export const calculateDateRange = (range: PREDEFINED_DATE_RANGE) => {
  const today = new Date();
  let fromDate: Date;
  let toDate: Date;

  switch (range) {
    case PREDEFINED_DATE_RANGE.TODAY:
      fromDate = today;
      toDate = today;
      break;

    case PREDEFINED_DATE_RANGE.YESTERDAY:
      fromDate = subDays(today, 1);
      toDate = subDays(today, 1);
      break;

    case PREDEFINED_DATE_RANGE.THIS_WEEK:
      fromDate = startOfWeek(today, { weekStartsOn: 1 });
      toDate = endOfWeek(today, { weekStartsOn: 1 });
      break;

    case PREDEFINED_DATE_RANGE.THIS_MONTH:
      fromDate = startOfMonth(today);
      toDate = endOfMonth(today);
      break;

    case PREDEFINED_DATE_RANGE.LAST_MONTH:
      fromDate = startOfMonth(subMonths(today, 1));
      toDate = endOfMonth(subMonths(today, 1));
      break;

    case PREDEFINED_DATE_RANGE.THIS_QUARTER:
      fromDate = startOfQuarter(today);
      toDate = endOfQuarter(today);
      break;

    case PREDEFINED_DATE_RANGE.THIS_YEAR:
      fromDate = startOfYear(today);
      toDate = endOfYear(today);
      break;

    case PREDEFINED_DATE_RANGE.LAST_QUARTER:
      fromDate = startOfQuarter(subQuarters(today, 1));
      toDate = endOfQuarter(subQuarters(today, 1));
      break;

    case PREDEFINED_DATE_RANGE.LAST_YEAR:
      fromDate = startOfYear(subYears(today, 1));
      toDate = endOfYear(subYears(today, 1));
      break;

    default:
      fromDate = startOfMonth(today);
      toDate = endOfMonth(today);
  }

  return {
    fromDate: format(fromDate, 'yyyy-MM-dd'),
    toDate: format(toDate, 'yyyy-MM-dd'),
  };
};

export const getPreDefinedDateRange = (startDate: Date, endDate: Date) => {
  const today = new Date();
  if (isSameDay(startDate, endDate)) {
    if (isToday(startDate)) {
      return PREDEFINED_DATE_RANGE.TODAY;
    }
    if (isYesterday(startDate)) {
      return PREDEFINED_DATE_RANGE.YESTERDAY;
    }
  }
  if (
    isSameDay(startDate, startOfWeek(today, { weekStartsOn: 1 })) &&
    isSameDay(endDate, endOfWeek(today, { weekStartsOn: 1 }))
  ) {
    return PREDEFINED_DATE_RANGE.THIS_WEEK;
  }
  if (
    isSameDay(startDate, startOfMonth(today)) &&
    isSameDay(endDate, endOfMonth(today))
  ) {
    return PREDEFINED_DATE_RANGE.THIS_MONTH;
  }
  if (
    isSameDay(startDate, startOfMonth(subMonths(today, 1))) &&
    isSameDay(endDate, endOfMonth(subMonths(today, 1)))
  ) {
    return PREDEFINED_DATE_RANGE.LAST_MONTH;
  }
  if (
    isSameDay(startDate, startOfQuarter(today)) &&
    isSameDay(endDate, endOfQuarter(today))
  ) {
    return PREDEFINED_DATE_RANGE.THIS_QUARTER;
  }
  if (
    isSameDay(startDate, startOfQuarter(subQuarters(today, 1))) &&
    isSameDay(endDate, endOfQuarter(subQuarters(today, 1)))
  ) {
    return PREDEFINED_DATE_RANGE.LAST_QUARTER;
  }
  if (
    isSameDay(startDate, startOfYear(today)) &&
    isSameDay(endDate, endOfYear(today))
  ) {
    return PREDEFINED_DATE_RANGE.THIS_YEAR;
  }
  if (
    isSameDay(startDate, startOfYear(subYears(today, 1))) &&
    isSameDay(endDate, endOfYear(subYears(today, 1)))
  ) {
    return PREDEFINED_DATE_RANGE.LAST_YEAR;
  }

  return null;
};

/**
 * Validates that a date range is valid (start date <= end date)
 * @param startDate - The start date string (yyyy-MM-dd format) or undefined
 * @param endDate - The end date string (yyyy-MM-dd format) or undefined
 * @returns true if the range is valid, false otherwise
 */
export const isValidDateRange = (
  startDate: string | undefined,
  endDate: string | undefined
): boolean => {
  if (!startDate || !endDate) {
    return true; // If either is missing, consider it valid (partial range)
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  return start <= end;
};

type FormatOptions = {
  roundMinutes?: boolean;
};
export function baseFormatDate(
  v: DateTimeValue,
  formatStr: string,
  options?: FormatOptions
): string {
  if (!v) return '';

  let date = new Date(v);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  if (options?.roundMinutes) {
    date = roundToNearestMinutes(date);
  }

  return format(date, formatStr);
}

/**
 * Formats a date-time value into 'dd MMM yyyy HH:mm' format.
 * @param value - Date-like value (Date, timestamp, ISO string).
 * @param options - Optional format options.
 * @returns Formatted date-time string or empty string when invalid.
 */
export const formatDateTime = (
  v: DateTimeValue,
  options?: FormatOptions
): string => {
  return baseFormatDate(v, 'dd MMM yyyy HH:mm', options);
};

/**
 * Formats a date-time value into 'HH:mm' format.
 * @param value - Date-like value (Date, timestamp, ISO string).
 * @param options - Optional format options.
 * @returns Formatted time string or empty string when invalid.
 */
export const formatTime = (
  v: DateTimeValue,
  options?: FormatOptions
): string => {
  return baseFormatDate(v, 'HH:mm', options);
};

/**
 * Formats a date-time value into 'dd/MM/yyyy' format.
 * @param value - Date-like value (Date, timestamp, ISO string).
 * @param options - Optional format options.
 * @returns Formatted date-time string or empty string when invalid.
 */
export const formatDate = (
  v: DateTimeValue,
  options?: FormatOptions
): string => {
  return baseFormatDate(v, 'dd/MM/yyyy', options);
};

/**
 * Formats a date-time value into 'dd MMM yyyy' format.
 * @param value - Date-like value (Date, timestamp, ISO string).
 * @param options - Optional format options.
 * @returns Formatted date-time string or empty string when invalid.
 */
export const formatDateExtended = (
  v: DateTimeValue,
  options?: FormatOptions
): string => {
  return baseFormatDate(v, 'dd MMM yyyy', options);
};

/**
 * Formats a date-time value into 'EEEE, do MMM yyyy - HH:mm' format.
 * @param value - Date-like value (Date, timestamp, ISO string).
 * @param options - Optional format options.
 * @returns Formatted date-time string or empty string when invalid.
 */
export const formatDateTimeExtended = (
  v: DateTimeValue,
  options?: FormatOptions
): string => {
  return baseFormatDate(v, 'EEEE, do MMM yyyy - HH:mm', options);
};

export const formatRelativeTime = (date: Date): string => {
  return formatDistanceToNow(date, {
    addSuffix: true,
    includeSeconds: true,
  }).replace('about ', '');
};
